
import { useState, useEffect, useRef } from "react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";

interface User {
  id: string;
  full_name?: string;
  username?: string;
}

interface MentionSelectorProps {
  users: User[];
  onSelect: (user: User) => void;
  position: { top: number; left: number };
  searchTerm: string;
}

export function MentionSelector({ users, onSelect, position, searchTerm }: MentionSelectorProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const filteredUsers = users.filter(user => {
    const name = user.full_name || user.username || '';
    return name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  useEffect(() => {
    setSelectedIndex(0);
  }, [searchTerm]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, filteredUsers.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredUsers[selectedIndex]) {
          onSelect(filteredUsers[selectedIndex]);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [filteredUsers, selectedIndex, onSelect]);

  if (filteredUsers.length === 0) return null;

  return (
    <div
      ref={containerRef}
      className="absolute z-50 bg-popover border rounded-md shadow-lg w-64 max-h-48"
      style={{
        top: position.top + 'px',
        left: position.left + 'px',
      }}
    >
      <ScrollArea className="max-h-48">
        <div className="p-1">
          {filteredUsers.map((user, index) => (
            <div
              key={user.id}
              className={`flex items-center gap-2 p-2 rounded-sm cursor-pointer ${
                index === selectedIndex ? 'bg-accent' : 'hover:bg-accent/50'
              }`}
              onClick={() => onSelect(user)}
            >
              <Avatar className="h-6 w-6">
                <AvatarFallback>
                  {(user.full_name || user.username || 'U')
                    .split(' ')
                    .map(n => n[0])
                    .join('')
                    .toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm truncate">
                {user.full_name || user.username || 'Usuário'}
              </span>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
